import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useSEO } from "@/lib/seo";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  approved: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  declined: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  info_requested: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  revoked: "bg-zinc-200 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-300",
};

/**
 * Admin cross-cutting view: every portfolio access request. Supports
 * override approve/decline/revoke and shows the full audit trail per row.
 */
export default function AdminPortfolioRequests() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState({});
  const [audits, setAudits] = useState({});

  useSEO({ title: "Admin — Portfolio Access Requests | FinanceLocal", noIndex: true });

  const load = () => {
    api.get("/admin/portfolio-requests", { params: filter ? { status: filter } : {} })
      .then((r) => setItems(r.data.items || [])).catch(() => {});
  };
  useEffect(load, [filter]);

  const act = async (req, kind) => {
    let body = {};
    let extraPath = kind;
    if (kind === "decline") {
      const reason = window.prompt("Decline reason (optional):", "");
      body = { reason };
    }
    if (kind === "request-info") {
      const message = window.prompt("Info request message:", "");
      if (!message) return;
      body = { message };
    }
    try {
      await api.post(`/marketplace/access-requests/${req.request_id}/${extraPath}`, body);
      toast.success(`Request ${kind}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${kind}`);
    }
  };

  const toggleAudit = async (req) => {
    setExpanded((prev) => ({ ...prev, [req.request_id]: !prev[req.request_id] }));
    if (!audits[req.request_id]) {
      try {
        const r = await api.get(`/marketplace/access-requests/${req.request_id}/audit`);
        setAudits((prev) => ({ ...prev, [req.request_id]: r.data.audit_events }));
      } catch { /* silent */ }
    }
  };

  return (
    <div data-testid="page-admin-portfolio-requests" className="mx-auto max-w-7xl px-4 py-10">
      <div className="border-b border-border pb-6 mb-6">
        <div className="font-overline text-accent">Admin</div>
        <h1 className="font-display text-4xl mt-2">Portfolio Access Requests</h1>
        <p className="text-sm text-muted-foreground mt-2">Override any seller decision. Every action is captured in the audit trail below each row.</p>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap" data-testid="admin-req-filter">
        {["", "pending", "approved", "declined", "info_requested", "revoked"].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)} data-testid={`filter-${s || "all"}`}
            className={`px-3 py-1 text-xs uppercase tracking-wider border ${filter === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
            {s || "all"}
          </button>
        ))}
      </div>

      {items.length === 0 && <div className="border border-border p-8 text-center text-muted-foreground" data-testid="admin-req-empty">No requests match this filter.</div>}

      <div className="border border-border divide-y divide-border" data-testid="admin-req-list">
        {items.map((req) => (
          <div key={req.request_id} className="p-4" data-testid={`admin-req-row-${req.request_id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="font-mono-data text-xs text-muted-foreground">{req.request_id}</div>
                <div className="font-display text-lg">{req.company_name} <span className="text-muted-foreground font-sans text-sm">— {req.contact_name}</span></div>
                <div className="text-xs text-muted-foreground">Portfolio: {req.portfolio_id} · Buyer: {req.buyer_email}</div>
                {req.proof_of_funds && <div className="text-xs mt-2 max-w-2xl"><b>PoF:</b> {req.proof_of_funds}</div>}
                {req.message && <div className="text-xs italic text-muted-foreground mt-1">&ldquo;{req.message}&rdquo;</div>}
              </div>
              <div className="text-right space-y-2">
                <span className={`inline-block px-2 py-0.5 text-[10px] font-mono-data uppercase tracking-wider ${STATUS_COLORS[req.status] || ""}`} data-testid={`admin-req-status-${req.request_id}`}>
                  {req.status}
                </span>
                <div className="flex gap-1 flex-wrap justify-end">
                  {["pending", "info_requested"].includes(req.status) && (
                    <>
                      <Button variant="outline" size="sm" className="rounded-none text-xs h-8" onClick={() => act(req, "approve")} data-testid={`admin-approve-${req.request_id}`}>Approve</Button>
                      <Button variant="outline" size="sm" className="rounded-none text-xs h-8" onClick={() => act(req, "decline")} data-testid={`admin-decline-${req.request_id}`}>Decline</Button>
                      <Button variant="outline" size="sm" className="rounded-none text-xs h-8" onClick={() => act(req, "request-info")} data-testid={`admin-info-${req.request_id}`}>Ask Info</Button>
                    </>
                  )}
                  {req.status === "approved" && (
                    <Button variant="outline" size="sm" className="rounded-none text-xs h-8 text-red-600 border-red-300" onClick={() => act(req, "revoke")} data-testid={`admin-revoke-${req.request_id}`}>Revoke Access</Button>
                  )}
                  <Button variant="outline" size="sm" className="rounded-none text-xs h-8" onClick={() => toggleAudit(req)} data-testid={`admin-audit-toggle-${req.request_id}`}>Audit</Button>
                </div>
              </div>
            </div>
            {expanded[req.request_id] && (
              <div className="mt-3 border border-border bg-muted/30 p-3 text-xs space-y-1" data-testid={`admin-audit-${req.request_id}`}>
                {(audits[req.request_id] || req.audit_events || []).map((ev, i) => (
                  <div key={i} className="flex gap-3 font-mono-data">
                    <span className="text-muted-foreground">{new Date(ev.at).toISOString().slice(0, 19).replace("T", " ")}</span>
                    <b>{ev.event}</b>
                    <span>by {ev.actor_role}:{(ev.actor_id || "").slice(-6)}</span>
                    {ev.details && Object.keys(ev.details).length > 0 && <span className="text-muted-foreground">· {JSON.stringify(ev.details)}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
